import React, { useState, useLayoutEffect, useEffect, useMemo, useCallback } from 'react';
import { Modal, useModalContext } from '../common/Modal';
import { TextInput } from '../common/TextInput';
import { Select } from '../common/Select';
import { gql, useMutation } from '@apollo/client';
import { toast } from 'react-toastify';
import { AddOrEditTransactionModal_CreateTransactionMutation, AddOrEditTransactionModal_CreateTransactionMutationVariables, AddOrEditTransactionModal_UpdateTransactionMutation, AddOrEditTransactionModal_UpdateTransactionMutationVariables, CreateTransactionInput, OrderPage_OrderFragment, TransactionType, UpdateTransactionInput } from '../../../codegen/graphql';
import { OrderPageTransactions_CreateTransactionMutation, OrderPageTransactions_CreateTransactionMutationVariables, OrderPageTransactions_UpdateTransactionMutation, OrderPageTransactions_UpdateTransactionMutationVariables } from '../../../codegen/graphql';
import { useRequestManager } from '../common/RequestManager';
import handleGraphqlMutationResult from '../../lib/handleGraphqlMutationResult';
import dayjs from 'dayjs';

interface AddOrEditTransactionModalProps {
  order: OrderPage_OrderFragment;
  transactionId?: string;
  onClose: () => void;
  onSuccess: () => void;
}

export function AddOrEditTransactionModal({
  order,
  transactionId,
  onClose,
  onSuccess,
}: AddOrEditTransactionModalProps) {
  const isEditing = !!transactionId;
  const transaction = isEditing 
    ? order.transactions.find(t => t.id === transactionId)
    : null;

  return (
    <Modal
      onClose={onClose}
      title={isEditing ? "Edit Transaction" : "Add Transaction"}
      width="medium"
    >
      <AddOrEditTransactionModalContent
        order={order}
        transaction={transaction}
        onClose={onClose}
        onSuccess={onSuccess}
      />
    </Modal>
  );
}

function AddOrEditTransactionModalContent({
  order,
  transaction,
  onClose,
  onSuccess,
}: {
  order: OrderPage_OrderFragment;
  transaction?: OrderPage_OrderFragment['transactions'][0] | null;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const modalContext = useModalContext();
  const { trackRequest } = useRequestManager();
  const isEditing = !!transaction;

  const [createTransaction] = useMutation<
    AddOrEditTransactionModal_CreateTransactionMutation,
    AddOrEditTransactionModal_CreateTransactionMutationVariables
  >(AddOrEditTransactionModal_CreateTransaction, {
    refetchQueries: ['OrderPage_GetExistingOrder'],
  });

  const [updateTransaction] = useMutation<
    AddOrEditTransactionModal_UpdateTransactionMutation,
    AddOrEditTransactionModal_UpdateTransactionMutationVariables
  >(AddOrEditTransactionModal_UpdateTransaction, {
    refetchQueries: ['OrderPage_GetExistingOrder'],
  });

  // Form state
  const [form, setForm] = useState((): UpdateTransactionInput | CreateTransactionInput => {
    if (isEditing) {
      return {
        orderId: Number(order.id),
        lineItemId: transaction!.lineItemId,
        type: transaction!.type,
        currency: transaction!.currency,
        amount: transaction!.amount,
        amountVat: transaction!.amountVat,
        billDate: transaction!.billDate,
        comment: transaction!.comment,
        source: transaction!.source,
        originCountry: transaction!.originCountry,
        destinationCountry: transaction!.destinationCountry,
        key: transaction!.key,
        isConfirmed: transaction!.isConfirmed,
      };
    }
    else {
      return {
        orderId: Number(order.id),
        lineItemId: null,
        type: TransactionType.ProductCost,
        currency: 'EUR',
        amount: 0,
        amountVat: 0,
        billDate: new Date().toISOString(),
        comment: null,
        source: 'manual',
        originCountry: order.customerOriginalAddressCountryCode,
        destinationCountry: order.customerAddressCountryCode,
        key: null,
        isConfirmed: false,
      };
    }
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // TODO Validation
  
    if (isEditing && transaction) {
      await trackRequest(
        updateTransaction({
          variables: {
            id: transaction.id,
            input: form,
          },
        })
          .then(handleGraphqlMutationResult),
        {
          title: 'Updating transaction',
          isBlocking: true,
        }
      );
    } else {
      await trackRequest(
        createTransaction({
          variables: {
            input: form as CreateTransactionInput,
          },
        })
          .then(handleGraphqlMutationResult),
        {
          title: 'Creating transaction',
          isBlocking: true,
        }
      );
    }

    onSuccess();
    onClose();
  };

  // Set modal actions
  useLayoutEffect(() => {
    modalContext.setActions([
      {
        label: 'Cancel',
        onClick: onClose,
        preset: 'white',
      },
      {
        label: isEditing ? 'Update' : 'Create',
        onClick: handleSubmit,
        preset: 'primary',
      },
    ]);
  }, [form, isEditing, modalContext, onClose]);

  const currencyOptions = [
    { value: 'EUR', label: 'EUR' },
  ];

  const typeOptions = [
    { value: TransactionType.ProductCost, label: 'Product Cost' },
    { value: TransactionType.ShippingCost, label: 'Shipping Cost' },
    { value: TransactionType.ChannelFee, label: 'Channel Fee' },
    { value: TransactionType.GrossSale, label: 'Gross Sale' },
    { value: TransactionType.RefundGrossSale, label: 'Refund Gross Sale' },
    { value: TransactionType.RefundChannelFee, label: 'Refund Channel Fee' },
    { value: TransactionType.RefundProductCost, label: 'Refund Product Cost' },
    { value: TransactionType.ReturnShippingCost, label: 'Return Shipping Cost' },
    { value: TransactionType.Other, label: 'Other' },
  ];

  const sourceOptions = [
    { value: 'manual', label: 'Manual' },
    { value: 'platform', label: 'Platform' },
    { value: 'supplier', label: 'Supplier' },
    { value: 'shipping', label: 'Shipping' },
    { value: 'other', label: 'Other' },
  ];

  const lineItemOptions = useMemo(() => {
    const base = [{ value: '', label: 'None' }];
    const lineItems = order.orderProductsVariations ?? [];
    const fromLineItems = lineItems.map((opv) => ({
      value: opv.id,
      label: `${opv.id}: ${opv.adVariation?.ad?.title ?? '—'}`,
    }));
    return [...base, ...fromLineItems];
  }, [order.orderProductsVariations]);

  const handleLineItemChange = useCallback((lineItemId: string) => {
    const lineItem = order.orderProductsVariations?.find(opv => opv.id === lineItemId);
    if (!lineItem) {
      return;
    }

    setForm({
      ...form,
      lineItemId: Number(lineItemId),
      originCountry: lineItem.supplierProductVariation?.supplierProduct?.originCountry ?? '',
      destinationCountry: order.customerAddressCountryCode,
    });
  }, [form]);

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <Select
        label="Linked line item"
        options={lineItemOptions}
        value={form.lineItemId?.toString() ?? ''}
        onChange={(value) => handleLineItemChange(value)}
        placeholder="Select a line item"
      />

      {form.lineItemId && (
        <>
        <p className="text-sm text-gray-500">
          Origin country: {form.originCountry}
        </p>
        <p className="text-sm text-gray-500">
          Destination country: {form.destinationCountry}
        </p>
        <Select
          label="Type"
          options={typeOptions}
          value={form.type ?? undefined}
          onChange={(value) => setForm({ ...form, type: value })}
          required
        />

        <Select
          label="Currency"
          options={currencyOptions}
          value={form.currency ?? ''}
          onChange={(value) => setForm({ ...form, currency: value })}
          required
        />

        <TextInput
          label="Amount"
          type="number"
          step="0.01"
          value={form.amount?.toString() ?? ''}
          onChange={(e) => setForm({ ...form, amount: Number(e.target.value) })}
          required
          placeholder="0.00"
        />

        <TextInput
          label="Amount VAT"
          type="number"
          step="0.01"
          value={form.amountVat?.toString() ?? ''}
          onChange={(e) => setForm({ ...form, amountVat: Number(e.target.value) })}
          placeholder="0.00"
        />

        <TextInput
          label="Bill Date"
          type="datetime-local"
          value={form.billDate}
          onChange={(e) => setForm({ ...form, billDate: dayjs(e.target.value).toISOString() })}
          required
        />

        <Select
          label="Source"
          options={sourceOptions}
          value={form.source ?? ''}
          onChange={(value) => setForm({ ...form, source: value })}
          required
        />

        <TextInput
          label="Comment"
          value={form.comment ?? ''}
          onChange={(e) => setForm({ ...form, comment: e.target.value })}
          multiline
          rows={3}
          placeholder="Optional comment"
        />

        <div className="flex items-center">
          <input
            type="checkbox"
            id="isConfirmed"
            checked={form.isConfirmed ?? false}
            onChange={(e) => setForm({ ...form, isConfirmed: e.target.checked })}
            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
          />
          <label htmlFor="isConfirmed" className="ml-2 block text-sm text-gray-700">
            Is Confirmed
          </label>
        </div>
      </>)}
    </form>
  );
}




const AddOrEditTransactionModal_CreateTransaction = gql`
  mutation AddOrEditTransactionModal_CreateTransaction($input: CreateTransactionInput!) {
    createTransaction(input: $input) {
      id
      orderId
      lineItemId
      type
      currency
      amount
      amountVat
      billDate
      comment
      source
      isConfirmed
      createdAt
      updatedAt
    }
  }
`;

const AddOrEditTransactionModal_UpdateTransaction = gql`
  mutation AddOrEditTransactionModal_UpdateTransaction($id: ID!, $input: UpdateTransactionInput!) {
    updateTransaction(id: $id, input: $input) {
      id
      orderId
      lineItemId
      type
      currency
      amount
      amountVat
      billDate
      comment
      source
      isConfirmed
      createdAt
      updatedAt
    }
  }
`;
